import React from 'react';
import { Show, useRecordContext, useTranslate } from 'react-admin';
import { Box, Card, CardContent, Typography } from '@mui/material';
import { AlertStateChip, SeverityChip } from './AlertChips';
import { MarkHandledButton } from './MarkHandledButton';
import { detailValue, handledLine, when } from './alertText';
import type { KioskAlert } from './types';

const Row = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.75, borderBottom: '1px solid #F0F0F0' }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" component="div" sx={{ textAlign: 'right', wordBreak: 'break-word' }}>{value}</Typography>
  </Box>
);

export const KioskAlertDetail = () => {
  const record = useRecordContext<KioskAlert>();
  const translate = useTranslate();
  if (!record) return null;
  const t = (key: string) => translate(`resources.kiosk-alerts.${key}`);
  const details = Object.entries(record.details);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: { xs: 1, sm: 2 } }}>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
            <SeverityChip severity={record.severity} />
            <AlertStateChip status={record.status} />
            <Typography variant="h6">{record.label}</Typography>
          </Box>
          <Typography variant="body2" sx={{ mb: 2 }}>{record.summary}</Typography>
          <Row label={t('fields.kiosk')} value={record.kioskName} />
          <Row label={t('fields.site')} value={record.siteName} />
          <Box sx={{ mt: 2 }}>
            <MarkHandledButton record={record} />
          </Box>
        </CardContent>
      </Card>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
        <Card sx={{ flex: 1, minWidth: 0 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1 }}>{t('sections.timeline')}</Typography>
            <Row label={t('timeline.raised')} value={when(record.raisedAt)} />
            {record.handledAt !== null && <Row label={t('timeline.handled')} value={handledLine(record, translate)} />}
            {record.handledNote !== null && <Row label={t('timeline.note')} value={record.handledNote} />}
            {record.clearedAt !== null && <Row label={t('timeline.cleared')} value={when(record.clearedAt)} />}
          </CardContent>
        </Card>
        <Card sx={{ flex: 1, minWidth: 0 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1 }}>{t('sections.details')}</Typography>
            {details.length === 0
              ? <Typography variant="body2" color="text.secondary">{t('noDetails')}</Typography>
              : details.map(([key, value]) => <Row key={key} label={key} value={detailValue(value)} />)}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export const KioskAlertShow = () => {
  const translate = useTranslate();
  return (
    <Show title={translate('resources.kiosk-alerts.name')}>
      <KioskAlertDetail />
    </Show>
  );
};

export default KioskAlertShow;
